<template>
    <div class="m-top-20 panel panel-info">
        <div v-if="showLink" class="modalDialog is-active" role="dialog" aria-labelledby="modal-title" aria-describedby="modal-description" aria-hidden="true" @keyup.esc="modalClose">
            <div class="modal-contents">
                <div>
                    <a title="Close" class="close" @click="showLink=false">X</a>
                    <h3><slot id="modal-title" name="header">View Edit</slot></h3>
                    <hr />
                </div>
                <div>
                    <slot id="modal-description" name="body">
                        <span class="bigger">
                            Click <a target="_edits" :href="'/oasis/viewer/view/archive/' + this.link" @click="showLink=false">HERE</a> to View.
                        </span>
                    </slot>
                    <hr />
                </div>
                <div class="right">
                    <button class="btn btn-primary" @click="showLink=false">Close</button>
                </div>
                <div class="clear"></div>
            </div>
        </div>
        <div class="panel-heading centered bold big ">
            <div class="col-xs-4 centered bigger">
                Edits
            </div>
            <div class="col-xs-2 p-sides-0">
                <div class="col-xs-5 p-sides-0">
                    <span class="smaller-label">Date Start</span>
                </div>
                <div class="col-xs-7 p-sides-0">
                    <date-field
                        name="start_date"
                        @changedate="updateCalendar('searchQueryDateStart', $event)"
                        v-model="searchQueryDateStart"
                        ref="searchQueryDateStart"
                        placeholder="Date Start"
                        :format="dateFormat"
                    />
                </div>
            </div>
            <div class="col-xs-2 p-sides-0">
                <div class="col-xs-5 p-sides-0">
                    <span class="smaller-label">Date End</span>
                </div>
                <div class="col-xs-7 p-sides-0">
                    <date-field
                        name="end_date"
                        @changedate="updateCalendar('searchQueryDateEnd', $event)"
                        v-model="searchQueryDateEnd"
                        ref="searchQueryDateEnd"
                        placeholder="Date End"
                        :format="dateFormat"
                    />
                </div>
            </div>
            <div class="right" style="display: inline-flex;">
                <span class="p-right-5"><div v-if="loading" class="inline-loader"></div></span>
                <span class="btn btn-primary" @click="getEdits()" ><strong>Search</strong></span> 
                <span class="btn btn-default" @click="resetSearch"><strong>Reset</strong></span> 
            </div>
            <div class="clear"></div>
        </div>
        <ag-grid
            :name="name"
            :ref="reference"
            :iteration="iteration"
            :width="width"
            :height="height"
            :columns="columnDefs"
            :row-data="rowData"
            :options="gridOptions"
            sidebar="filters"
            :filter-order="filterOrder"
            :classes="'ag-theme-alpine ' + (loading ? 'opacity-25' : '')"
        />
    </div>
</template>

<script>
import 'regenerator-runtime/runtime';
import Axios from '../classes/Axios.js';
import DateField from './DateField.vue';
import AgGrid from './AgGrid.vue';
import {defineComponent} from 'vue';

export default defineComponent ({
    name: "Edits",

    emits: ['changed'],

    components: {
        'date-field': DateField,
        'ag-grid': AgGrid,
    },

    props: {
        payrollId: {
            type: [String, Number],
            required: false,
        },
        entityId: {
            type: [String, Number],
            required: false,
        },
        name: {
            type: String,
            required: false,
            default: 'edits',
        },
        reference: {
            type: String,
            required: false,
            default: 'edits',
        },
        classes: {
            type: [ String, Array ],
            required: false,
            default: 'form-control'
        },
        propayFrontend: {
            type: String,
            required: true
        },
    },

    data() {
        return {
            waiting: false, // whole-page spinner
            loading: false, // spinner
            iteration: 0,
            proPayPayrollId: null,
            searchQueryDateStart: undefined,
            searchQueryDateEnd: undefined,
            locale: window.navigator.userLanguage || window.navigator.language || 'en-US', // 'fr-fr' // test stuff
            oasisApi: new Axios('/restv2'),
            dateFormat: 'MM/dd/yyyy',
            reportType: 'invoice',
            link: '',
            showLink: false,

            // AG Grid
            rowData: [],
            columnDefs: undefined,
            sidebar: undefined,
            filterOrder: undefined,
            gridOptions: {
                singleClickEdit: false,
                undoRedoCellEditing: false,
                enterMovesDown: false,
                enterMovesDownAfterEdit: false,
                rowSelection: false, // 'multiple',
                suppressContextMenu: true,
                defaultColDef: {
                    resizable: true,
                    sortable: true,
                    menuTabs: [],
                    suppressSizeToFit: false,
                    headerCheckboxSelectionFilteredOnly: true,
                    filterParams: {
                        suppressMiniFilter: true,
                        defaultToNothingSelected: true,
                        buttons: ['reset', 'apply'],
                        debounceMs: 200
                    }
                },
                onGridReady: (event) => this.$refs[this.reference].onGridReady(event),
                onCellClicked: (event) => {
                    // if clicked on first row, load the edit
                    if (event.column.getColId() === 'id' && event && event.data && event.data.id) {
                        this.getEdit(event.data.id)
                    }
                },
            }
        }
    },

    computed: {
        width() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return '100%';
        },

        height() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return window.innerHeight * 0.63 + 'px';
        },

        today() {
            let date = new Date();
            var day = date.getDate();
            var monthIndex = date.getMonth();
            var year = date.getFullYear();

            return year + '-' + this.pad(monthIndex * 1 + 1) + '-' + this.pad(day);
        },
    },

    methods: {
        async getEdits() {
            this.loading = true;
            this.rowData = undefined;
            const endpoint = '/edits'
            let query = {};

            if (this.payrollId) query.payroll= this.payrollId;
            if (this.searchQueryDateStart) query.date__ge = new Date(this.searchQueryDateStart).toISOString().split('T')[0];
            if (this.searchQueryDateEnd) query.date__le = new Date(this.searchQueryDateEnd).toISOString().split('T')[0];

            if (Object.keys(query).length < 2) {
                this.createDialog({ "title": "Error", "body": 'Please enter a search option such as Employee or a date range', "buttonNo": false, "buttonYesLabel": "OK" });
                this.loading = false;
                return false;
            }

            let queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&');
            // console.log(query, queryString);
            try {
                const response = await this.oasisApi.getAllPages(endpoint + '?' + queryString);
                // console.log(response);
                if (response && response.data && response.data.results) {
                    this.rowData = response.data.results;
                } else {
                    this.rowData = [];
                }
                this.$emit('changed', this.rowData);
            } catch (error) {
                console.log(error);
                this.loading = false;
                return false;
            };
            this.iteration++;
            this.loading = false;
        },

        async getEdit(payrollId) {
            if (!payrollId) return false;
            this.loading = true;

            const endpoint = `/edits/${payrollId}/${this.reportType}`;
            const response = await this.oasisApi.get(endpoint);
            // console.log(response.data);
            if (response && response.data) {
                this.link = response.data;
                this.showLink = true;
            }
            this.loading = false;
        },

        resetSearch() {
            const now = new Date();
            const today = now.toISOString().split('T')[0];
            const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 31).toISOString().split('T')[0];
            if (this.rowData) this.rowData = undefined;
            this.searchQueryDateStart = startDate;
            this.searchQueryDateEnd = today;
        },

    },

    created() {
        this.columnDefs = [
            {
                headerName: 'Invoice #',
                field: 'id',
                colId: 'id',
                maxWidth: 420,
                minWidth: 200,
                editable: false,
                sortable: true,
                filter: true,
                cellRenderer: (params) => params.data.number ? `<span class="fakelink">${params.data.number}</span>` : '',
                menuTabs: [],
            },
            {
                headerName: 'WE Date',
                colId: 'pay_period_end_date',
                maxWidth: 250,
                minWidth: 100,
                width: 150,
                editable: false,
                sortable: true,
                valueFormatter: (params) => params.data.pay_period_end_date,
                menuTabs: [],
            },
            {
                headerName: 'Check Date',
                colId: 'check_date',
                maxWidth: 250,
                minWidth: 100,
                width: 150,
                editable: false,
                sortable: true,
                valueFormatter: (params) => params.data.check_date,
                menuTabs: [],
            },
            {
                headerName: 'Description',
                colId: 'description',
                maxWidth: 700,
                minWidth: 350,
                editable: false,
                sortable: true,
                valueFormatter: (params) => params.data.description,
                menuTabs: [],
            },
            {
                headerName: 'Amount',
                colId: 'amount_decimal',
                maxWidth: 300,
                minWidth: 100,
                width: 200,
                cellClass: 't-right bold',
                editable: false,
                sortable: true,
                valueFormatter: (params) => parseFloat(params.data.amount_decimal).formatNumber('$'),
                menuTabs: [],
            },
        ]
    },

    async mounted() {
        setTimeout(() => { $('[data-toggle="tooltip"]').tooltip() }, 500);

        // Prevent back button from failing.
        history.pushState(null, document.referrer, location.href);
        window.onpopstate = function () {
            window.location.href = document.referrer;
        };

        this.resetSearch();
        this.getEdits();
    }
});
</script>
